import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TicketDetailModal = ({ ticket, isOpen, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTicket, setEditedTicket] = useState(ticket || {});
  const [newResponse, setNewResponse] = useState('');
  const [responses, setResponses] = useState([
    {
      id: 1,
      author: 'Sarah Johnson',
      authorAvatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
      message: 'I have reviewed the authentication issue and identified the root cause. The problem appears to be with the SAML integration for enterprise users.',
      timestamp: '2025-01-26T10:15:00Z',
      type: 'response',
      isInternal: false
    },
    {
      id: 2,
      author: 'Michael Chen',
      authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
      message: 'Internal note: This affects approximately 150 enterprise users. Escalating to engineering team.',
      timestamp: '2025-01-26T11:30:00Z',
      type: 'internal_note',
      isInternal: true
    }
  ]);

  if (!isOpen || !ticket) return null;

  const handleSave = () => {
    onUpdate?.(editedTicket);
    setIsEditing(false);
  };

  const handleAddResponse = () => {
    if (!newResponse.trim()) return;
    
    const response = {
      id: responses.length + 1,
      author: 'Current Admin',
      authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
      message: newResponse,
      timestamp: new Date().toISOString(),
      type: 'response',
      isInternal: false
    };
    
    setResponses([...responses, response]);
    setNewResponse('');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-error bg-error/10 border-error/20';
      case 'high': return 'text-warning bg-warning/10 border-warning/20';
      case 'medium': return 'text-primary bg-primary/10 border-primary/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-error bg-error/10 border-error/20';
      case 'in-progress': return 'text-warning bg-warning/10 border-warning/20';
      case 'pending': return 'text-text-secondary bg-muted border-border';
      case 'resolved': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Ticket" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">{ticket.id}</h2>
              <p className="text-sm text-text-secondary">{ticket.customer}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={isEditing ? "default" : "outline"}
              size="sm"
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              iconName={isEditing ? "Save" : "Edit"}
            >
              {isEditing ? "Save Changes" : "Edit Ticket"}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Panel - Ticket Details */}
          <div className="w-1/2 p-6 border-r border-border overflow-y-auto">
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Title</label>
                {isEditing ? (
                  <Input
                    value={editedTicket.title || ''}
                    onChange={(e) => setEditedTicket({...editedTicket, title: e.target.value})}
                    className="w-full"
                  />
                ) : (
                  <p className="text-text-primary font-medium">{ticket.title}</p>
                )}
              </div>

              {/* Status and Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Status</label>
                  {isEditing ? (
                    <select
                      value={editedTicket.status || ticket.status}
                      onChange={(e) => setEditedTicket({...editedTicket, status: e.target.value})}
                      className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Priority</label>
                  {isEditing ? (
                    <select
                      value={editedTicket.priority || ticket.priority}
                      onChange={(e) => setEditedTicket({...editedTicket, priority: e.target.value})}
                      className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                        getPriorityColor(ticket.priority || "low") // default low
                      }`}
                    >
                      {(ticket.priority || "low")
                        .charAt(0)
                        .toUpperCase() + (ticket.priority || "low").slice(1)}
                    </span>
                  )}
                </div>
              </div>

              {/* Assignee */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Assignee</label>
                {isEditing ? (
                  <select
                    value={editedTicket.assignee || ticket.assignee}
                    onChange={(e) => setEditedTicket({...editedTicket, assignee: e.target.value})}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Sarah Johnson">Sarah Johnson</option>
                    <option value="Michael Chen">Michael Chen</option>
                    <option value="Emily Rodriguez">Emily Rodriguez</option>
                    <option value="David Kim">David Kim</option>
                    <option value="Lisa Thompson">Lisa Thompson</option>
                  </select>
                ) : (
                  <div className="flex items-center space-x-2">
                    <img
                      src={ticket.assigneeAvatar}
                      alt={ticket.assignee}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-text-primary">{ticket.assignee}</span>
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Category</label>
                {isEditing ? (
                  <select
                    value={editedTicket.category || ticket.category}
                    onChange={(e) => setEditedTicket({...editedTicket, category: e.target.value})}
                    className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="Authentication">Authentication</option>
                    <option value="Performance">Performance</option>
                    <option value="Feature Request">Feature Request</option>
                    <option value="Integration">Integration</option>
                    <option value="Documentation">Documentation</option>
                    <option value="Bug Report">Bug Report</option>
                  </select>
                ) : (
                  <span className="inline-block px-2 py-1 bg-muted rounded text-sm text-text-secondary">
                    {ticket.category}
                  </span>
                )}
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Created</label>
                  <p className="text-sm text-text-primary">{formatTimeAgo(ticket.created)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Last Update</label>
                  <p className="text-sm text-text-primary">{formatTimeAgo(ticket.lastUpdate)}</p>
                </div>
              </div>

              {/* SLA Status */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">SLA Status</label>
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={ticket.status === 'breach' ? 'AlertTriangle' : 'CheckCircle'} 
                    size={16} 
                    className={ticket.status === 'breach' ? 'text-error' : 'text-success'} 
                  />
                  <span className={`text-sm font-medium ${ticket.slaStatus === 'breach' ? 'text-error' : 'text-success'}`}>
                    {ticket.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Responses */}
          <div className="w-1/2 flex flex-col">
            {/* Responses Header */}
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-text-primary">Responses & Updates</h3>
              <p className="text-sm text-text-secondary">Communication history and internal notes</p>
            </div>

            {/* Responses List */}
            <div className="flex-1 p-6 overflow-y-auto space-y-4">
              {responses.map((response) => (
                <div key={response.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <img
                      src={response.authorAvatar}
                      alt={response.author}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-text-primary">{response.author}</span>
                          {response.isInternal && (
                            <span className="px-2 py-1 bg-warning/10 text-warning border border-warning/20 rounded text-xs font-medium">
                              Internal
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-text-secondary">{formatTimeAgo(response.timestamp)}</span>
                      </div>
                      <p className="text-sm text-text-primary">{response.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* New Response Form */}
            <div className="p-6 border-t border-border">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-text-secondary">Add Response</label>
                <textarea
                  value={newResponse}
                  onChange={(e) => setNewResponse(e.target.value)}
                  placeholder="Type your response here..."
                  className="w-full h-24 px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary placeholder-text-secondary resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="internal-note"
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <label htmlFor="internal-note" className="text-sm text-text-secondary">
                      Internal note (not visible to customer)
                    </label>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAddResponse}
                    disabled={!newResponse.trim()}
                    iconName="Send"
                  >
                    Send Response
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailModal;